/**
 * How the person last moved through the dashboard, kept on `<html data-input>`: "pointer" after a press with a mouse,
 * a pen or a finger, "keyboard" after a key that moves the focus or acts (Tab, the arrows, Enter, Space, a letter).
 * Escape leaves the state as it was: it closes a popup a click opened, and the opener the focus goes back to keeps no
 * ring for it (a keyboard user who opened the popup with a key is in the keyboard state already). globals.css draws focus outlines and rings for the keyboard only, so an element focused by a press, or by a
 * script right after one (a dialog's first button as it opens, the opener as it closes), never keeps one. A key held
 * with Ctrl, Alt or Cmd (a shortcut, a screenshot) leaves the state as it was. Before the first press or key the
 * keyboard counts, so nothing hides a focus a keyboard user needs.
 */

/** Keys that leave the state as it was: the modifiers on their own, and Escape, which closes rather than moves. */
const KEPT = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock", "Fn", "OS", "Escape"]);

export type InputModality = "pointer" | "keyboard";

/** The way of the last press or key. */
export function inputModality(): InputModality {
  return typeof document !== "undefined" && document.documentElement.dataset.input === "pointer"
    ? "pointer"
    : "keyboard";
}

/**
 * Whether an element shows its focus: it matches `:focus-visible` and the keyboard moved last. A chart's hover mark or
 * a tooltip that opens on focus asks this, so a focus a script gives back after a click draws nothing.
 */
export function isKeyboardFocus(el: Element | null | undefined): boolean {
  if (!el || inputModality() === "pointer") {
    return false;
  }
  try {
    return el.matches(":focus-visible");
  } catch {
    return true;
  }
}

/** Start following the presses and keys; returns the function that stops it. */
export function trackInputModality(root: HTMLElement = document.documentElement): () => void {
  const set = (value: InputModality): void => {
    if (root.dataset.input !== value) {
      root.dataset.input = value;
    }
  };
  const onPointer = (): void => set("pointer");
  const onKey = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || KEPT.has(event.key)) {
      return;
    }
    set("keyboard");
  };
  document.addEventListener("pointerdown", onPointer, true);
  document.addEventListener("keydown", onKey, true);
  return () => {
    document.removeEventListener("pointerdown", onPointer, true);
    document.removeEventListener("keydown", onKey, true);
    delete root.dataset.input;
  };
}
